import { Link } from 'react-router-dom';
import { PublicSiteFrame } from '../components/PublicSiteFrame';

/**
 * Public landing page at portal.paznetworks.com root.
 *
 * Marketing surface aligned with paznetworks.com — dark navy header,
 * rust accent headline, cool-gray hero. No customer data, no external
 * API calls.
 */
export function LandingPage() {
  return (
    <PublicSiteFrame>
      <main>
        <section className="public-hero">
          <div className="public-hero-grid">
            <div>
              <p className="public-eyebrow">
                <span className="public-eyebrow-rule" aria-hidden />
                Coast-to-coast · Customer portal
              </p>
              <h1 className="public-headline">
                <span className="public-headline-accent">Managed IT, security, and AV</span>
                {' '}for every site you operate.
              </h1>
              <p className="public-lede">
                See live health, tickets, documents, and field work across your
                locations — with a freshness chip on every number so stale data
                is never dressed up as current.
              </p>
              <div className="public-cta-row">
                <Link to="/login" className="public-btn-primary">
                  Sign in to your portal
                  <span aria-hidden className="public-btn-arrow">
                    →
                  </span>
                </Link>
                <a
                  href="https://paznetworks.com"
                  target="_blank"
                  rel="noreferrer"
                  className="public-btn-secondary"
                >
                  Learn about PazNetworks
                </a>
              </div>
            </div>

            {/* Illustrative only: names the surfaces, never invents figures. */}
            <div className="public-hero-visual" aria-hidden>
              <div className="public-float-card public-float-card--top">
                <p className="public-float-kicker">Portfolio</p>
                <p className="public-float-value">Every account, one login</p>
                <p className="public-float-meta">Switch sites without signing out</p>
              </div>
              <div className="public-hero-panel">
                <div className="public-hero-panel-bar">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="public-hero-panel-body">
                  <div className="public-stat">
                    <span>Network</span>
                    <strong>Per-site telemetry</strong>
                  </div>
                  <div className="public-stat">
                    <span>Tickets</span>
                    <strong>Tracked to close</strong>
                  </div>
                  <div className="public-stat">
                    <span>SLA</span>
                    <strong>Measured, not assumed</strong>
                  </div>
                </div>
              </div>
              <div className="public-float-card public-float-card--bottom">
                <p className="public-float-kicker">Freshness</p>
                <p className="public-float-value public-float-value--ok">Timestamped</p>
                <p className="public-float-meta">Stale data is labelled as stale</p>
              </div>
            </div>
          </div>
        </section>

        <section className="public-features">
          <div className="public-features-inner">
            <p className="public-eyebrow">
              <span className="public-eyebrow-rule" aria-hidden />
              Built for multi-site operators
            </p>
            <h2 className="public-section-title">
              One portal for the systems PazNetworks runs for you.
            </h2>
            <div className="public-feature-grid">
              <Feature
                title="Live device health"
                body="Every location shows device status with a freshness chip. When polling lags, the chip says so — no green lights on stale data."
              />
              <Feature
                title="Tickets and field reports"
                body="See every ticket, install-day report, document, and as-built without chasing email threads."
              />
              <Feature
                title="Operational trends"
                body="Month-over-month uptime, resolution time, and asset refresh — the same numbers your account manager reviews."
              />
              <Feature
                title="Your data, nothing extra"
                body="Internal notes, cost basis, and confidential intel are stripped before they reach this portal."
              />
              <Feature
                title="Auditable"
                body="Every change is recorded. We can prove what changed, when, and by whom."
              />
              <Feature
                title="Ready on a phone"
                body="Check site status, sign off on field reports, or raise a ticket from the floor."
              />
            </div>
          </div>
        </section>

        <section className="public-band">
          <div className="public-band-inner">
            <h2>Your access is provisioned by your PazNetworks account manager.</h2>
            <p>If you don’t have credentials yet, contact your account team.</p>
            <Link to="/login" className="public-btn-primary">
              Sign in
              <span aria-hidden className="public-btn-arrow">
                →
              </span>
            </Link>
          </div>
        </section>
      </main>
    </PublicSiteFrame>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <article className="public-feature-card">
      <h3>{title}</h3>
      <p>{body}</p>
    </article>
  );
}
